/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from './index';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ children, heading, lines, resources, isCalculatingTotal }) => {
    const shipping = lines?.shipping
        ? React.cloneElement(lines.shipping as React.ReactElement, {
            price: 0
        })
        : null;
    const totalDiscounts = lines?.totalDiscounts
        ? React.cloneElement(lines.totalDiscounts as React.ReactElement, {
            price: Math.abs((lines.totalDiscounts as React.ReactElement).props.price)
        })
        : null;

    return (
        <div className='msc-order-summary-wrapper'>
            <Node className='msc-order-summary__items'>
                {heading}
                <div className='msc-order-summary__items'>
                    {lines && (
                        <>
                            {lines.subtotal}
                            {shipping}
                            {lines.otherCharge}
                            {lines.tax}
                            {totalDiscounts}
                            {lines.loyalty}
                            {lines.customerAccount}
                            {lines.giftCard}
                            {!isCalculatingTotal && lines.orderTotal}
                            {isCalculatingTotal && (
                                <p className='msc-order-summary__line-calculating'>
                                    {}
                                    <span>{resources.amountToPayText}</span>
                                    <span style={{ float: 'right' }}>
                                        <span>{resources.calculatingText}...</span>
                                    </span>
                                </p>
                            )}
                            <p className='msc-order-summary__line-container-deposit'>
                                <span>* {resources.containerDepositText}</span>
                            </p>
                        </>
                    )}
                </div>
                {children && (
                    <Node className='msc-order-summary__children'>
                        {children}
                    </Node>
                )}
            </Node>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines, isCalculatingTotal }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

export const JSONStringifyCheckoutCart: React.FC<{ data: ICheckoutViewProps['data'] | undefined }> = ({ data }) => {
    const checkout = data?.checkout;

    if (!checkout?.result?.checkoutCart?.cart) {
        return null;
    }

    let json: string | undefined;

    try {
        json = JSON.stringify({
            Id: checkout.result.checkoutCart.cart.Id,
            DeliveryMode: checkout.result.checkoutCart.cart.DeliveryMode,
            RequestedDeliveryDate: checkout.result.checkoutCart.cart.RequestedDeliveryDate,
            CartLines: checkout.result.checkoutCart.cart.CartLines?.map(cartLine => ({
                ItemId: cartLine.ItemId,
                DeliveryMode: cartLine.DeliveryMode,
                RequestedDeliveryDate: cartLine.RequestedDeliveryDate
            }))
        }, null, 2);
    } catch (e) {
        json = undefined;
    }

    if (!json) {
        return null;
    }

    return (
        <div className='sr-only' id='checkout-cart-json' aria-hidden>
            <div style={{ whiteSpace: 'pre' }}>
                {json}
            </div>
        </div>
    );
};

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        isCalculatingTotal,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        sideProps,
        sideControlFirstProps,
        sideControlThirdProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        calculateTotalButton,
        placeOrderButton,
        missingRequestedDeliveryDateError,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        splitDeliveryModal
    } = props;

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                            </Node>
                            <Node {...sideProps}>
                                {props.reminderMessage}
                                {!hasInvoiceLine
                                    ? orderSummary && (
                                        <OrderSummaryComponent {...orderSummary} resources={props.resources} isCalculatingTotal={isCalculatingTotal}>
                                            {keepShoppingButton}
                                            {calculateTotalButton}
                                            {placeOrderButton}
                                            {missingRequestedDeliveryDateError}
                                        </OrderSummaryComponent>
                                    )
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} isCalculatingTotal={isCalculatingTotal} />}
                                {termsAndConditions && (
                                    <Node {...sideControlFirstProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    </Node>
                                )}
                                {splitDeliveryModal?.Container && (
                                    <Node {...sideControlThirdProps}>
                                        {splitDeliveryModal && (
                                            <Node {...splitDeliveryModal.Container}>
                                                {splitDeliveryModal.header}
                                                {splitDeliveryModal.message}
                                                {splitDeliveryModal.button}
                                                {splitDeliveryModal.modal}
                                            </Node>
                                        )}
                                    </Node>
                                )}
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
            <JSONStringifyCheckoutCart data={props.data} />
        </Module>
    );
};

export default CheckoutView;
